/**
 * ticker-track — Custom element for the marquee ticker.
 *
 * Fixes two CSS-only limitations of the -50% translateX loop approach:
 *
 *  1. Gap misalignment — flex `gap` adds an extra gap between the last
 *     element of pass 1 and the first element of pass 2, making -50% land
 *     slightly off. This is fixed by measuring the exact period (one pass
 *     width + one computed gap) and setting --ticker-period in pixels.
 *
 *  2. Track-width — the track must be ≥ 2× viewport width at all times or
 *     the right side goes empty mid-animation. This is fixed by cloning the
 *     original items until the total width exceeds 2.5× viewport width.
 *
 * Graceful degradation: the Liquid template still renders a static second
 * pass (marked data-ticker-clone). If this script fails to load, those two
 * passes and the CSS -50% fallback keep the ticker functional.
 *
 * Font timing: setup is deferred until document.fonts.ready so measurements
 * reflect the actual loaded web font metrics, not the fallback font.
 */
class TickerTrack extends HTMLElement {
  connectedCallback() {
    this._originals = [...this.children].filter(
      (el) => !el.hasAttribute('data-ticker-clone')
    );

    const init = () => requestAnimationFrame(() => this._setup());

    if (document.fonts?.ready) {
      document.fonts.ready.then(init);
    } else {
      init();
    }

    this._ro = new ResizeObserver(() => {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this._setup(), 150);
    });
    this._ro.observe(this.closest('.ticker__viewport') || this);
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    clearTimeout(this._debounce);
  }

  _setup() {
    if (!this._originals?.length) return;

    // Remove all clones (both Liquid-rendered and previously JS-added)
    this.querySelectorAll('[data-ticker-clone]').forEach((el) => el.remove());

    // Measure the exact width of one original pass
    const first = this._originals[0];
    const last = this._originals[this._originals.length - 1];
    const passWidth = last.getBoundingClientRect().right - first.getBoundingClientRect().left;

    if (passWidth <= 0) return;

    // Period = one pass width + one trailing gap (the missing piece that makes
    // -50% imprecise — adding this gap makes the reset frame-perfect)
    const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
    const period = passWidth + gap;

    // Clone originals until total track width ≥ 2.5× viewport
    const vpWidth = (this.closest('.ticker__viewport') || this).offsetWidth;
    let total = passWidth;

    while (total < vpWidth * 2.5) {
      this._originals.forEach((original) => {
        const clone = original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-ticker-clone', '');
        [...clone.attributes]
          .filter((attr) => attr.name.startsWith('data-shopify'))
          .forEach((attr) => clone.removeAttribute(attr.name));
        this.appendChild(clone);
      });
      total += passWidth;
    }

    // Apply the pixel-accurate translation and restart animation from frame 0
    this.style.setProperty('--ticker-period', `-${period}px`);
    this.style.animationName = 'none';
    void this.offsetWidth; // force reflow — browser re-initialises the animation
    this.style.animationName = '';
  }
}

customElements.define('ticker-track', TickerTrack);
